'use client';

import React from 'react';
import debounce from 'lodash/debounce';
import { classNames } from '@repo/ui/src/utils/classNames';
import LeftChevron from '@repo/ui/src/components/Icons/LeftChevron';
import RightChevron from '@repo/ui/src/components/Icons/RightChevron';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { useTranslation } from '@repo/ui/src/context/Translation/useTranslation';

interface Props {
  url: string;
  count: number;
  className?: string;
  disableMobilePointerEvents?: boolean;
  ariaLabel: string;
  children?: React.ReactNode;
  showSrInstructions?: boolean;
}

const SpinViewer = ({
  url,
  count,
  className,
  ariaLabel,
  children,
  disableMobilePointerEvents,
  showSrInstructions = true,
}: Props) => {
  const { t } = useTranslation();
  const [currentFrame, setCurrentFrame] = React.useState(0);
  const [loaded, setLoaded] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStartX = React.useRef(0);
  const dragStartFrame = React.useRef(0);

  // Generate image URLs
  const imageUrls = React.useMemo(() => {
    return Array.from({ length: count }, (_, i) => `${url}/${i + 1}.webp`);
  }, [url, count]);

  // Preload all images
  React.useEffect(() => {
    if (count === 0) return;

    let loadedCount = 0;
    const images = imageUrls.map((src) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        loadedCount++;
        if (loadedCount === count) {
          handleLoaded();
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === count) {
          handleLoaded();
        }
      };
      return img;
    });

    return () => {
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [imageUrls, count]);

  const handleLoaded = React.useCallback(
    debounce(() => {
      setLoaded(true);
    }, 100),
    [ariaLabel],
  );

  const handlePanLeft = () => setCurrentFrame((prev) => (prev + 1) % count);
  const handlePanRight = () => setCurrentFrame((prev) => (prev - 1 + count) % count);

  // Calculate new frame based on drag delta
  const calculateFrameFromDrag = React.useCallback(
    (clientX: number) => {
      const deltaX = clientX - dragStartX.current;
      const sensitivity = 3; // pixels per frame
      const frameDelta = Math.floor(deltaX / sensitivity);

      let newFrame = (dragStartFrame.current - frameDelta) % count;
      if (newFrame < 0) newFrame = count + newFrame;

      return newFrame;
    },
    [count],
  );

  // Mouse drag handlers (desktop - always enabled)
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartFrame.current = currentFrame;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const newFrame = calculateFrameFromDrag(e.clientX);
    setCurrentFrame(newFrame);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch drag handlers (mobile - respects disableMobilePointerEvents)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (disableMobilePointerEvents) return;
    setIsDragging(true);
    dragStartX.current = e.touches[0].clientX;
    dragStartFrame.current = currentFrame;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const newFrame = calculateFrameFromDrag(e.touches[0].clientX);
    setCurrentFrame(newFrame);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Cleanup on unmount
  React.useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePanLeft();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handlePanRight();
    }
  };

  return (
    <section
      aria-label={t('productDetailPageContent.spinViewerRegionLabel', undefined, {
        default: '360° product image viewer',
      })}
    >
      {showSrInstructions && (
        <span className="sr-only">
          {t('productDetailPageContent.spinInstructions', undefined, {
            default:
              'Use the left and right arrow buttons or drag the image to rotate the product 360 degrees.',
          })}
        </span>
      )}
      <div className="relative flex justify-center items-center h-full">
        <div className="flex justify-center lg:justify-start align-middle flex-col h-full">
          <div
            className={classNames(
              `sm:pointer-events-auto sm:cursor-grab sm:active:cursor-grabbing max-h-[60vh] sm:max-h-[80vh] mt-10 lg:mt-8 select-none`,
              className,
              disableMobilePointerEvents && 'pointer-events-none',
              !loaded && 'hidden',
              isDragging && 'cursor-grabbing',
            )}
            tabIndex={0}
            aria-label={`${ariaLabel} — view ${currentFrame + 1} of ${count}`}
            onKeyDown={handleKeyDown}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {imageUrls.map((src, index) => (
              <img
                key={src}
                src={src}
                alt={`${ariaLabel} — view ${index + 1}`}
                aria-hidden={index !== currentFrame ? 'true' : undefined}
                className={classNames(
                  'w-full h-full object-contain max-h-[60vh] sm:max-h-[80vh]',
                  index === currentFrame ? 'block' : 'hidden',
                )}
                draggable={false}
              />
            ))}
          </div>

          {loaded && (
            <>
              {children ? (
                <div className="grid grid-flow-col items-center justify-center gap-3 z-20">
                  <Button
                    onClick={handlePanLeft}
                    aria-label="Pan left"
                    variant={ButtonVariant.bare}
                    size="small"
                    className="hover:text-neutral-200 text-xl"
                  >
                    &lt;
                  </Button>
                  {children}
                  <Button
                    onClick={handlePanRight}
                    aria-label="Pan right"
                    variant={ButtonVariant.bare}
                    size="small"
                    className="hover:text-neutral-200 text-xl"
                  >
                    &gt;
                  </Button>
                </div>
              ) : (
                <>
                  <div className="absolute left-4 sm:bottom-1/2 sm:-translate-y-1/2 z-10 -bottom-8">
                    <Button
                      onClick={handlePanLeft}
                      aria-label="Pan left"
                      variant={ButtonVariant.bare}
                      size="none"
                      className="bg-neutral-100 hover:bg-neutral-200 text-warm-500 rounded-full p-1 sm:p-2 shadow-lg transition-colors"
                    >
                      <LeftChevron darkMode className="w-8 h-8" aria-hidden="true" />
                    </Button>
                  </div>
                  <div className="absolute right-4 sm:bottom-1/2 sm:-translate-y-1/2 z-10 -bottom-8">
                    <Button
                      onClick={handlePanRight}
                      aria-label="Pan right"
                      variant={ButtonVariant.bare}
                      size="none"
                      className="bg-neutral-100 hover:bg-neutral-200 text-warm-500 rounded-full p-1 sm:p-2 shadow-lg transition-colors"
                    >
                      <RightChevron darkMode className="w-8 h-8" aria-hidden="true" />
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default SpinViewer;
